import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from './Modal';
import { fetchDatasets } from '../services/datasetsService';
import './DatasetSelectModal.css';

const DatasetSelectModal = ({ isOpen, onClose, onSelect }) => {
  const [allDatasets, setAllDatasets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  // Fetch datasets once when modal opens
  const loadDatasets = useCallback(async () => {
    if (allDatasets) return; // already loaded
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDatasets();
      setAllDatasets(data);
    } catch (err) {
      console.error('Failed to load datasets:', err);
      setError(err.message || 'Failed to load datasets');
    } finally {
      setLoading(false);
    }
  }, [allDatasets]);

  useEffect(() => {
    if (isOpen) {
      loadDatasets();
      setQuery('');
      // Focus search input after modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setAllDatasets(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter datasets by search query (only show results when query has 1+ chars)
  const filteredDatasets = query.length > 0 && allDatasets
    ? allDatasets.filter(d =>
        d.name.toLowerCase().includes(query.toLowerCase()) ||
        d.id.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select Dataset">
      <div className="dataset-select-modal">
        <div className="dataset-search-container">
          <input
            ref={inputRef}
            className="dataset-search-input"
            type="text"
            placeholder="Search datasets..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading && <div className="dataset-loading">Loading datasets...</div>}
        {error && <div className="dataset-error">Error: {error}</div>}

        {!loading && !error && query.length === 0 && (
          <div className="dataset-search-hint">Type to search datasets</div>
        )}

        {!loading && !error && query.length > 0 && (
          <div className="dataset-list">
            {filteredDatasets.length === 0 ? (
              <div className="dataset-search-hint">No datasets matching "{query}"</div>
            ) : (
              filteredDatasets.map((dataset) => (
                <div
                  key={dataset.id}
                  className="dataset-item"
                  onClick={() => onSelect(dataset)}
                >
                  <div className="dataset-info">
                    <h3>{dataset.name}</h3>
                    <p className="dataset-meta">
                      {dataset.jsonFileCount} files
                    </p>
                  </div>
                  <div className="dataset-select-arrow">&rarr;</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DatasetSelectModal;
